import { createContext, FC, PropsWithChildren, useContext, useEffect } from 'react';
import io, { Socket } from 'socket.io-client';
import { getPwaToken } from '../utilities/authCookie';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { chatApi } from '../store/api/chatApi';
import { addMessageIfMissing, applySeenMessages, removeMessageById } from '../store/api/chatSocketCache';
import type { ChatRoomItemsInterface, DeleteMessagePayloadInterface } from '../components/chat/ChatRoom';
import { chatList } from '../actions/chatListActions';
import { store } from '../ReduxRoot';
import type { ChatListDataInterface } from '../components/chat/ChatListItemsMobile';

const pwaCookie = process.env.PWA_COOKIE_NAME || 'pwa_staging_session';

const socket = io(`${process.env.REACT_APP_CHAT_API_BASE_URL}`, {
	autoConnect: false,
	reconnection: true,
	transports: ['websocket'],
});
let socketToken = '';

const SocketContext = createContext<Socket>(socket);

export const useSocket = (): Socket => {
 	const context = useContext(SocketContext);
	if (!context) {
		throw new Error('useSocket must be used within a SocketProvider');
	}
	return context;
};

export const connectSocket = (socketClient: Socket): boolean => {
	const token = getPwaToken();
	if (!token) {
		socketToken = '';
		return false;
	}

	if (socketClient.connected && socketToken !== token) {
		socketClient.disconnect();
	}
	socketClient.auth = { token };
	socketToken = token;
	if (!socketClient.connected) {
		socketClient.connect();
	}
	return true;
};

type Props = PropsWithChildren;

export const SocketProvider: FC<Props> = ({ children }) => {
 	const loggedInUserDetails = useAppSelector((state) => state.loggedInUserDetails);
	const dispatch = useAppDispatch();

	useEffect(() => {
		const handleConnect = () => {
			socket.emit('INIT', { token: getPwaToken() });
		};
		const handleConnectError = (error: Error) => {
			console.warn('Chat socket connection failed:', error.message);
		};
		const handleNewMessage = (message: ChatRoomItemsInterface) => {
			dispatch(
				chatApi.util.updateQueryData(
					'getChatMessages',
					{ chatId: message.chatId, offset: 0, limit: 25 },
					(draft) => addMessageIfMissing(draft, message)
				)
			);
			dispatch(chatApi.util.invalidateTags([{ type: 'Chat', id: 'LIST' }]));
			void dispatch(chatApi.endpoints.getChatDetails.initiate(message.chatId, { forceRefetch: true }))
				.unwrap()
				.then((chat) => {
					const currentChats = store.getState().chatList as ChatListDataInterface[];
					dispatch(chatList([chat, ...currentChats.filter((currentChat) => `${currentChat.id}` !== `${chat.id}`)]));
				})
				.catch((error) => console.warn('Unable to refresh chat list item:', error));
		};
		const handleSeenMessages = (messages: ChatRoomItemsInterface[]) => {
			if (messages.length === 0) {
				return;
			}
			const chatId = messages[0].chatId;
			dispatch(
				chatApi.util.updateQueryData('getChatMessages', { chatId, offset: 0, limit: 25 }, (draft) =>
					applySeenMessages(draft, messages)
				)
			);
			dispatch(chatApi.util.invalidateTags([{ type: 'ChatRoom', id: chatId }]));
		};
		const handleDeleteMessage = ({ chatId, messageId }: DeleteMessagePayloadInterface) => {
			dispatch(
				chatApi.util.updateQueryData('getChatMessages', { chatId, offset: 0, limit: 25 }, (draft) =>
					removeMessageById(draft, messageId)
				)
			);
			dispatch(chatApi.util.invalidateTags([{ type: 'ChatRoom', id: chatId }]));
		};
		const handleDeleteAllMessages = ({ chatId }: { chatId: number | string }) => {
			dispatch(
				chatApi.util.updateQueryData('getChatMessages', { chatId, offset: 0, limit: 25 }, () => [])
			);
			dispatch(
				chatApi.util.invalidateTags([
					{ type: 'ChatRoom', id: chatId },
					{ type: 'PinnedMessage', id: 'LIST' },
				])
			);
		};

		socket.on('connect', handleConnect);
		socket.on('connect_error', handleConnectError);
		socket.on('NEW_MESSAGE', handleNewMessage);
		socket.on('NEW_SEEN_MESSAGES', handleSeenMessages);
		socket.on('DELETE_MESSAGE', handleDeleteMessage);
		socket.on('DELETE_ALL_MESSAGES', handleDeleteAllMessages);
		if (!connectSocket(socket)) {
			socket.disconnect();
		}

		return () => {
			socket.off('connect', handleConnect);
			socket.off('connect_error', handleConnectError);
			socket.off('NEW_MESSAGE', handleNewMessage);
			socket.off('NEW_SEEN_MESSAGES', handleSeenMessages);
			socket.off('DELETE_MESSAGE', handleDeleteMessage);
			socket.off('DELETE_ALL_MESSAGES', handleDeleteAllMessages);
		};
	}, [dispatch, loggedInUserDetails]);
	return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
};
